import { FC } from "react";
import { Box, Grid, Stack, Typography } from "@mui/material";
import { FormProvider, useForm } from "react-hook-form";
import { Input } from "@/components/elements/Input";
import { InputDatePicker } from "@/components/elements/InputDatePicker";
import { YEAR_MONTH_FORMAT } from "@/constants/datetime";
import { text } from "@/theme/colors";
import { TExamStepsFilterFormExtended } from "@/features/exam-steps/components/filter/exam-steps-filter-shared";
import { useExamStepsFilterMasterData } from "@/features/exam-steps/components/filter/use-exam-steps-filter-master-data";
import { ComboboxMulti } from "@/components/elements/Combobox";

type ExamStepsFilterFormFieldsProps = {
  filterValues: TExamStepsFilterFormExtended;
  onFilterChange: (field: keyof TExamStepsFilterFormExtended, value: unknown) => void;
};

// Layout matches design: 4 rows × 2 cols (last row only has 登録日 on the left when 所属機関 hidden,
// or 登録日 + empty when shown). 所属機関 is RSO-only — hidden for other user types.
// Wraps in a dummy FormProvider so child components reading useFormContext() don't crash.
export const ExamStepsFilterFormFields: FC<ExamStepsFilterFormFieldsProps> = ({ filterValues, onFilterChange }) => {
  const { options, canFilterByOrganization } = useExamStepsFilterMasterData();
  const methods = useForm();

  return (
    <FormProvider {...methods}>
      <Grid container rowSpacing={2} columnSpacing={3}>
        {/* Row 1 */}
        <Grid item xs={6}>
          <Stack spacing={0.5}>
            <Typography variant="cap12Light">氏名</Typography>
            <Input
              nonControl
              placeholder="入力してください"
              value={filterValues.fullName}
              onChange={(e) => onFilterChange("fullName", (e.target as HTMLInputElement).value)}
            />
          </Stack>
        </Grid>
        {canFilterByOrganization && (
          <Grid item xs={6}>
            <Stack spacing={0.5}>
              <Typography variant="cap12Light">所属機関</Typography>
              <ComboboxMulti
                placeholder="選択してください"
                value={filterValues.rsoCompanyIds}
                onChange={(v) => onFilterChange("rsoCompanyIds", v)}
                options={options.organization}
              />
            </Stack>
          </Grid>
        )}

        {/* Row 2 */}
        <Grid item xs={6}>
          <Stack spacing={0.5}>
            <Typography variant="cap12Light">国籍</Typography>
            <ComboboxMulti
              placeholder="選択してください"
              value={filterValues.nationalityIds}
              onChange={(v) => onFilterChange("nationalityIds", v)}
              options={options.nationality}
            />
          </Stack>
        </Grid>
        <Grid item xs={6}>
          <Stack spacing={0.5}>
            <Typography variant="cap12Light">手続きステータス</Typography>
            <ComboboxMulti
              placeholder="選択してください"
              value={filterValues.procedureStatuses}
              onChange={(v) => onFilterChange("procedureStatuses", v)}
              options={options.procedureStatus}
            />
          </Stack>
        </Grid>

        {/* Row 3 */}
        <Grid item xs={6}>
          <Stack spacing={0.5}>
            <Typography variant="cap12Light">次の手続き期日・期限</Typography>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Box sx={{ flex: 1 }}>
                <InputDatePicker
                  placeholder="選択してください"
                  value={filterValues.nextProcedureEndDateFrom}
                  onChange={(value) => onFilterChange("nextProcedureEndDateFrom", value)}
                  nonControl
                />
              </Box>
              <Typography variant="cap12" sx={{ color: text.secondary }}>
                〜
              </Typography>
              <Box sx={{ flex: 1 }}>
                <InputDatePicker
                  placeholder="選択してください"
                  value={filterValues.nextProcedureEndDateTo}
                  onChange={(value) => onFilterChange("nextProcedureEndDateTo", value)}
                  nonControl
                />
              </Box>
            </Stack>
          </Stack>
        </Grid>
        <Grid item xs={6}>
          <Stack spacing={0.5}>
            <Typography variant="cap12Light">試験年月</Typography>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Box sx={{ flex: 1 }}>
                <InputDatePicker
                  placeholder="選択してください"
                  value={filterValues.examMonthFrom}
                  onChange={(value) => onFilterChange("examMonthFrom", value)}
                  views={["year", "month"]}
                  openTo="month"
                  format={YEAR_MONTH_FORMAT}
                  nonControl
                />
              </Box>
              <Typography variant="cap12" sx={{ color: text.secondary }}>
                〜
              </Typography>
              <Box sx={{ flex: 1 }}>
                <InputDatePicker
                  placeholder="選択してください"
                  value={filterValues.examMonthTo}
                  onChange={(value) => onFilterChange("examMonthTo", value)}
                  views={["year", "month"]}
                  openTo="month"
                  format={YEAR_MONTH_FORMAT}
                  nonControl
                />
              </Box>
            </Stack>
          </Stack>
        </Grid>

        {/* Row 4 */}
        <Grid item xs={6}>
          <Stack spacing={0.5}>
            <Typography variant="cap12Light">登録日</Typography>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Box sx={{ flex: 1 }}>
                <InputDatePicker
                  placeholder="選択してください"
                  value={filterValues.registeredAtFrom}
                  onChange={(value) => onFilterChange("registeredAtFrom", value)}
                  nonControl
                />
              </Box>
              <Typography variant="cap12" sx={{ color: text.secondary }}>
                〜
              </Typography>
              <Box sx={{ flex: 1 }}>
                <InputDatePicker
                  placeholder="選択してください"
                  value={filterValues.registeredAtTo}
                  onChange={(value) => onFilterChange("registeredAtTo", value)}
                  nonControl
                />
              </Box>
            </Stack>
          </Stack>
        </Grid>
      </Grid>
    </FormProvider>
  );
};
